import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import {
  createTemplateField,
  getNextCampoAdicionadoKey,
  insertFieldToken,
  removeFieldTokensByUuid,
  normalizeFieldKey,
  updateFieldTokenKeyByUuid,
  syncBracketFieldsInEditor,
  syncBracketFieldsInEditorDeferred,
  validateFieldsForSave,
} from '../extensions/fieldTokenEditorUtils';
import { buildTemplateSavePayload } from '../../../utils/buildTemplateSavePayload';
import { saveUserTemplate } from '../../../services/templateService';
import {
  getTemplateNameInputError,
  normalizeTemplateName,
} from '../utils/templateNameUtils';

const TemplateEditorContext = createContext(null);

export function TemplateEditorProvider({
  children,
  templateId: initialTemplateId,
  initialName = '',
  initialFields = [],
  initialContent = '<p></p>',
  editorMountKey = 'new',
  isEditMode = false,
}) {
  const [templateId] = useState(() => initialTemplateId || uuidv4());
  const [fields, setFields] = useState(() => initialFields ?? []);
  const [templateName, setTemplateName] = useState(initialName);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasSavedSuccessfully, setHasSavedSuccessfully] = useState(isEditMode);
  const [activeFieldUuid, setActiveFieldUuid] = useState(null);
  const editorRef = useRef(null);
  const fieldsRef = useRef(initialFields ?? []);
  const savedBaselineRef = useRef(null);
  const [editorRevision, setEditorRevision] = useState(0);

  const captureSavedBaseline = useCallback(() => {
    const currentEditor = editorRef.current;
    savedBaselineRef.current = {
      name: normalizeTemplateName(templateName),
      fields: JSON.stringify(fieldsRef.current),
      content: currentEditor
        ? JSON.stringify(currentEditor.getJSON())
        : JSON.stringify(initialContent),
    };
  }, [templateName, initialContent]);

  useEffect(() => {
    if (!editor || savedBaselineRef.current != null) return;
    captureSavedBaseline();
  }, [editor, captureSavedBaseline]);

  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  const registerEditor = useCallback((instance) => {
    editorRef.current = instance;
    setEditor(instance);
  }, []);

  useEffect(() => {
    const currentEditor = editor;
    if (!currentEditor || currentEditor.isDestroyed) return undefined;

    const onUpdate = () => {
      setEditorRevision((revision) => revision + 1);
    };

    currentEditor.on('update', onUpdate);
    return () => {
      if (!currentEditor.isDestroyed) {
        currentEditor.off('update', onUpdate);
      }
    };
  }, [editor]);

  const ensureFieldForKey = useCallback((key) => {
    const normalizedKey = normalizeFieldKey(key);
    if (!normalizedKey) return null;

    const existing = fieldsRef.current.find((field) => field.key === normalizedKey);
    if (existing) return existing;

    const created = createTemplateField(normalizedKey, 'text', normalizedKey);
    setFields((prev) => {
      if (prev.some((field) => field.key === normalizedKey)) {
        return prev;
      }
      return [...prev, created];
    });
    fieldsRef.current = [...fieldsRef.current, created];
    return created;
  }, []);

  const ensureFieldRef = useRef(ensureFieldForKey);
  ensureFieldRef.current = ensureFieldForKey;

  const addFieldAtCursor = useCallback(() => {
    const currentEditor = editorRef.current;
    if (!currentEditor) return;

    const key = getNextCampoAdicionadoKey(fieldsRef.current);
    const field = createTemplateField(key, 'text');

    setFields((prev) => [...prev, field]);
    fieldsRef.current = [...fieldsRef.current, field];
    insertFieldToken(currentEditor, { uuid: field.uuid, key: field.key });
  }, []);

  const renameField = useCallback((uuid, newKey) => {
    const normalizedKey = normalizeFieldKey(newKey);
    if (!uuid || !normalizedKey) return;

    setFields((prev) =>
      prev.map((field) =>
        field.uuid === uuid ? { ...field, key: normalizedKey } : field
      )
    );

    updateFieldTokenKeyByUuid(editorRef.current, uuid, normalizedKey);
  }, []);

  const updateFieldType = useCallback((uuid, type) => {
    if (!uuid) return;
    setFields((prev) =>
      prev.map((field) => (field.uuid === uuid ? { ...field, type } : field))
    );
  }, []);

  const updateFieldName = useCallback((uuid, fieldName) => {
    if (!uuid) return;
    setFields((prev) =>
      prev.map((field) =>
        field.uuid === uuid ? { ...field, fieldName: String(fieldName ?? '') } : field
      )
    );
  }, []);

  const updateFieldObrigatorio = useCallback((uuid, obrigatorio) => {
    if (!uuid) return;
    setFields((prev) =>
      prev.map((field) =>
        field.uuid === uuid ? { ...field, obrigatorio: Boolean(obrigatorio) } : field
      )
    );
  }, []);

  const removeField = useCallback((uuid) => {
    if (!uuid) return;
    setFields((prev) => prev.filter((field) => field.uuid !== uuid));
    removeFieldTokensByUuid(editorRef.current, uuid);
  }, []);

  const registerFieldsFromKeys = useCallback((keys) => {
    const keyToUuid = new Map();
    const newFields = [];

    (keys ?? []).forEach((key) => {
      const normalized = normalizeFieldKey(key);
      if (!normalized || keyToUuid.has(normalized)) return;

      const existing = fieldsRef.current.find((field) => field.key === normalized);
      if (existing) {
        keyToUuid.set(normalized, existing.uuid);
        return;
      }

      const created = createTemplateField(normalized, 'text', normalized);
      newFields.push(created);
      keyToUuid.set(normalized, created.uuid);
    });

    if (newFields.length) {
      setFields((prev) => [...prev, ...newFields]);
      fieldsRef.current = [...fieldsRef.current, ...newFields];
    }

    return keyToUuid;
  }, []);

  const syncFieldsFromEditorContent = useCallback((deferred = false) => {
    const currentEditor = editorRef.current;
    if (!currentEditor) return { keys: [], replaced: 0 };

    if (deferred) {
      return syncBracketFieldsInEditorDeferred(
        currentEditor,
        registerFieldsFromKeys
      );
    }

    return syncBracketFieldsInEditor(currentEditor, registerFieldsFromKeys);
  }, [registerFieldsFromKeys]);

  const saveTemplate = useCallback(async () => {
    const normalizedName = normalizeTemplateName(templateName);
    if (!normalizedName) {
      toast.error('Informe o Nome do Modelo antes de salvar.');
      return { success: false };
    }

    const templateNameError = getTemplateNameInputError(normalizedName);
    if (templateNameError) {
      toast.error(templateNameError);
      return { success: false };
    }

    if (!String(templateId ?? '').trim()) {
      toast.error('ID do modelo inválido.');
      return { success: false };
    }

    const fieldErrors = validateFieldsForSave(fieldsRef.current);
    if (fieldErrors.length) {
      toast.error(fieldErrors[0]);
      return { success: false };
    }

    const currentEditor = editorRef.current;
    if (!currentEditor) {
      toast.error('Editor indisponível.');
      return { success: false };
    }

    const payload = buildTemplateSavePayload({
      id: templateId,
      name: normalizedName,
      fields: fieldsRef.current,
      content: currentEditor.getJSON(),
    });
    console.log('[saveTemplate] payload enviado ao backend:', payload);

    setSaving(true);
    try {
      const result = await saveUserTemplate(payload);
      if (!result.success) {
        toast.error(
          typeof result.error === 'string' ? result.error : 'Erro ao salvar modelo.'
        );
        return result;
      }

      setHasSavedSuccessfully(true);
      captureSavedBaseline();
      toast.success('Modelo salvo com sucesso.');
      return result;
    } catch (error) {
      console.error('saveTemplate', error);
      toast.error('Erro ao salvar modelo.');
      return { success: false };
    } finally {
      setSaving(false);
    }
  }, [templateId, templateName, captureSavedBaseline]);

  const isEditorDirty = useMemo(() => {
    const baseline = savedBaselineRef.current;
    if (!baseline) return false;

    const currentEditor = editorRef.current;
    const name = normalizeTemplateName(templateName);
    const fieldsJson = JSON.stringify(fieldsRef.current);
    const contentJson = currentEditor
      ? JSON.stringify(currentEditor.getJSON())
      : JSON.stringify(initialContent);

    return (
      name !== baseline.name ||
      fieldsJson !== baseline.fields ||
      contentJson !== baseline.content
    );
  }, [templateName, fields, editor, editorRevision, initialContent]);

  const focusFieldInPanel = useCallback((uuid) => {
    if (!uuid) return;
    setActiveFieldUuid(String(uuid));
  }, []);

  const clearActiveField = useCallback(() => {
    setActiveFieldUuid(null);
  }, []);

  const clearAllFields = useCallback(() => {
    fieldsRef.current = [];
    setFields([]);
    setActiveFieldUuid(null);
  }, []);

  const value = useMemo(
    () => ({
      fields,
      templateId,
      templateName,
      initialContent,
      editorMountKey,
      editor,
      saving,
      hasSavedSuccessfully,
      activeFieldUuid,
      setTemplateName,
      registerEditor,
      ensureFieldForKey,
      ensureFieldRef,
      addFieldAtCursor,
      renameField,
      updateFieldType,
      updateFieldName,
      updateFieldObrigatorio,
      removeField,
      clearAllFields,
      syncFieldsFromEditorContent,
      saveTemplate,
      focusFieldInPanel,
      clearActiveField,
      isEditorDirty,
      isEditMode,
    }),
    [
      fields,
      templateId,
      templateName,
      initialContent,
      editorMountKey,
      editor,
      saving,
      hasSavedSuccessfully,
      activeFieldUuid,
      registerEditor,
      ensureFieldForKey,
      addFieldAtCursor,
      renameField,
      updateFieldType,
      updateFieldName,
      updateFieldObrigatorio,
      removeField,
      clearAllFields,
      syncFieldsFromEditorContent,
      saveTemplate,
      focusFieldInPanel,
      clearActiveField,
      isEditorDirty,
      isEditMode,
    ]
  );

  return (
    <TemplateEditorContext.Provider value={value}>
      {children}
    </TemplateEditorContext.Provider>
  );
}

export function useTemplateEditor() {
  const context = useContext(TemplateEditorContext);
  if (!context) {
    throw new Error('useTemplateEditor must be used within TemplateEditorProvider');
  }
  return context;
}

export default TemplateEditorContext;
